import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../../services/api';
import './AcceptInvitation.css';

/**
 * AcceptInvitation — invitation landing page.
 *
 * For Azure (SSO) users: validate token → show Microsoft login button.
 * For Local users: validate token → show password setup form → POST /invitation/set-password.
 */
const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // Local password setup state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await api.get(`/invitation/validate?token=${token}`);
        setUserInfo(response.data);
      } catch (err) {
        const message =
          err.response?.data?.detail || 'Invalid or expired invitation link.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleMicrosoftLogin = async () => {
    setSigningIn(true);
    try {
      const response = await api.get('/auth/microsoft/login', {
        params: { redirect_after: '/dashboard' },
      });
      const authUrl = response.data?.auth_url;
      if (!authUrl) throw new Error('No auth URL');
      window.location.href = authUrl;
    } catch (err) {
      setSigningIn(false);
      setError('Failed to initiate Microsoft sign-in. Please try again.');
    }
  };

  const checkRequirements = (pwd) => ({
    length: pwd.length >= 12,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    digit: /\d/.test(pwd),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  });

  const handleSetPassword = async (e) => {
    e.preventDefault();
    const errors = {};

    const reqs = checkRequirements(password);
    const missing = [];
    if (!reqs.length) missing.push('at least 12 characters');
    if (!reqs.upper) missing.push('an uppercase letter');
    if (!reqs.lower) missing.push('a lowercase letter');
    if (!reqs.digit) missing.push('a digit');
    if (!reqs.special) missing.push('a special character');
    if (missing.length) {
      errors.password = `Password must contain ${missing.join(', ')}.`;
    }

    if (!confirmPassword) {
      errors.confirm = 'Please confirm your password.';
    } else if (password !== confirmPassword) {
      errors.confirm = 'Passwords do not match.';
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    setError('');

    try {
      await api.post('/invitation/set-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reqs = checkRequirements(password);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="accept-invitation-form">
        <h2>Validating Invitation</h2>
        <div className="loading-container">
          <span className="spinner large"></span>
          <p>Please wait while we validate your invitation...</p>
        </div>
      </div>
    );
  }

  // ── Error (no user info) ─────────────────────────────────────────────────────
  if (error && !userInfo) {
    return (
      <div className="accept-invitation-form">
        <h2>Invitation Error</h2>
        <div className="error-container">
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#FF5630" strokeWidth="2" />
              <path d="M15 9L9 15M9 9L15 15" stroke="#FF5630" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <p className="error-message">{error}</p>
          <p className="error-help">Please contact your administrator for a new invitation.</p>
          <Link to="/login" className="btn btn-secondary" style={{ marginTop: '0px' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // ── Password set successfully ────────────────────────────────────────────────
  if (success) {
    return (
      <div className="accept-invitation-form">
        <h2>Account Activated!</h2>
        <div className="success-container">
          <div className="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#36B37E" strokeWidth="2" />
              <path d="M8 12L11 15L16 9" stroke="#36B37E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="success-message">Your account is ready.</p>
          <p className="success-help">
            You can now sign in with your email and password.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ marginTop: '0px' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  const isLocal = userInfo?.auth_provider === 'local';

  // ── Main invite page ─────────────────────────────────────────────────────────
  return (
    <div className="accept-invitation-form">
      <h2>You have been invited!</h2>

      <div className="user-info-card">
        <p className="welcome-text">Welcome, {userInfo?.first_name}!</p>
        <p className="email-text">
          Account invitation for <strong>{userInfo?.email}</strong>
        </p>
        <span className="role-badge">{userInfo?.role?.replace('_', ' ')}</span>
      </div>

      {error && <div className="form-error">{error}</div>}

      {isLocal ? (
        /* ── Local user: set password form ─────────────────────────────────── */
        <>
          <div className="invite-instructions">
            <p>Create a password to activate your account.</p>
          </div>

          <form onSubmit={handleSetPassword} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="inv-password">Password</label>
              <input
                id="inv-password"
                type="password"
                className={`form-input${fieldErrors.password ? ' error' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                autoComplete="new-password"
                disabled={submitting}
              />
              {fieldErrors.password && (
                <span className="field-error">{fieldErrors.password}</span>
              )}
            </div>

            {password.length > 0 && (
              <div className="password-requirements">
                <p className="requirements-title">Requirements</p>
                <ul>
                  <li className={reqs.length ? 'met' : ''}>At least 12 characters</li>
                  <li className={reqs.upper ? 'met' : ''}>At least one uppercase letter</li>
                  <li className={reqs.lower ? 'met' : ''}>At least one lowercase letter</li>
                  <li className={reqs.digit ? 'met' : ''}>At least one digit</li>
                  <li className={reqs.special ? 'met' : ''}>At least one special character</li>
                </ul>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="inv-confirm">Confirm Password</label>
              <input
                id="inv-confirm"
                type="password"
                className={`form-input${fieldErrors.confirm ? ' error' : ''}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={submitting}
              />
              {fieldErrors.confirm && (
                <span className="field-error">{fieldErrors.confirm}</span>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--space-2)' }}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)', width: '16px', height: '16px', marginRight: '8px' }} />
                  Activating account...
                </>
              ) : (
                'Activate Account'
              )}
            </button>
          </form>
        </>
      ) : (
        /* ── Azure user: Microsoft SSO button ──────────────────────────────── */
        <>
          <div className="invite-instructions">
            <p>
              To activate your account, sign in with your Microsoft account below.
              Your account will be activated automatically.
            </p>
          </div>

          <button
            type="button"
            className="btn-microsoft invite-ms-btn"
            onClick={handleMicrosoftLogin}
            disabled={signingIn}
            aria-label="Sign in with Microsoft to activate your account"
          >
            {signingIn ? (
              <>
                <span className="spinner spinner-dark" />
                Redirecting to Microsoft...
              </>
            ) : (
              <>
                <svg className="microsoft-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="21" height="21" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Sign in with Microsoft to Activate Account
              </>
            )}
          </button>

          <p className="login-sso-note">
            Use the Microsoft account associated with <strong>{userInfo?.email}</strong>.
          </p>
        </>
      )}
    </div>
  );
};

export default AcceptInvitation;
